// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, PageHeader } from 'grommet'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { Group, Previous } from 'grommet-icons'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { get, getErrorMessage } from '../../../utils/api-utils'
import {
  Anchor,
  Button,
  DataTable,
  Loader,
  NoDataInfo,
  Typography
} from '../../../components'
import { displayNotification } from '../../../utils/notificiation-utils'
import Markdown from '../../../commoncomponents/Markdown'
import { Layout } from '../../../commoncomponents/layout/Layout'
import { useVisibilityContext } from '../../../context/visibility-context'
import NoViewPermission from '../commoncomponents/NoViewPermission'
import useOrganizationAssociation from '../commoncomponents/useOrganizationAssociation'

import { GROUP_SCHEMAS } from './constants'
import CreateGroupModal from './components/CreateGroupModal'

const GroupsContent = ({ isWorkspace = false }) => {
  const { t } = useTranslation(['common', 'iam'])
  const location = useLocation()
  const navigate = useNavigate()
  const LDFlags = useFlags()
  const [userGroups, setUserGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('')
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false)
  // Pagination
  const [page, setPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [hasPermission, setHasPermission] = useState(false)
  const showScimServices = LDFlags['glcp-scim-idp-integration']
  const path = isWorkspace
    ? 'manage-account/identity'
    : 'manage-account/organization'
  const { v2RbacPolicies, v2RbacRootWkspcPolicies } = useVisibilityContext()
  const v2GroupViewPerm = v2RbacPolicies?.includes('identity.user-groups.read')
  const ccsAuthView = v2RbacPolicies?.includes('ccs.authorization.view')
  const ccsAssociateAuthView = v2RbacRootWkspcPolicies?.includes(
    'ccs.authorization.view'
  )
  const rootWkspReadPerm = v2RbacRootWkspcPolicies?.some((perm) =>
    [
      'identity.user-groups.read',
      'ccs.authorization.view',
      'ccs.authorization.edit'
    ]?.includes(perm)
  )
  const rootWkspReadPermOrgLevel = v2RbacRootWkspcPolicies?.includes(
    'identity.user-groups.read'
  )

  // Notifications
  const [notifTitle, setNotifTitle] = useState('')
  const [notifMsg, setNotifMsg] = useState('')
  const [notifSeverity, setNotifSeverity] = useState('')
  const setNotificationInfo = (message, severity, title = '') => {
    setNotifTitle(title)
    setNotifMsg(message)
    setNotifSeverity(severity)
  }

  const getPageIndexInfo = () => {
    const total = totalItems || 0
    if (total > 0) {
      const startIndex = 1 + (page - 1) * itemsPerPage
      const endIndex = itemsPerPage * page < total ? itemsPerPage * page : total
      return t('data_table_showing_label', {
        startIndex,
        endIndex,
        total
      })
    }
    return ''
  }

  const { isAssociated, isAssociateLoader } =
    useOrganizationAssociation(setNotificationInfo)

  const handleRowClick = (datum) => {
    navigate(`/${path}/groups/${datum?.id}`)
  }

  const handleBackBtnClick = () => {
    navigate(`/${path}`)
  }

  const columns = [
    {
      property: 'displayName',
      header: t('common:name'),
      primary: true,
      render: (datum) => {
        const displayName = datum?.displayName || '--'
        return (
          <Box direction="row" align="center" gap="small">
            <Typography
              type="text"
              wordBreak="break-all"
              testId="group-display-name"
              emphasis
            >
              {displayName}
            </Typography>
          </Box>
        )
      }
    },
    {
      property: 'description',
      header: t('common:description'),
      render: (datum) => {
        const description = showScimServices
          ? datum?.description
          : datum?.[GROUP_SCHEMAS?.PARTIAL]?.groupDescription
        return (
          <Typography type="text" testId="group-description">
            {description || '--'}
          </Typography>
        )
      }
    },
    ...(showScimServices
      ? [
          {
            property: 'source',
            header: t('iam:igc_users.source'),
            render: (datum) => {
              return datum?.source?.toUpperCase() === 'LOCAL' || isWorkspace ? (
                <Typography type="text" testId="group-user-source">
                  {datum?.source}
                </Typography>
              ) : (
                <Anchor
                  label={
                    <Button
                      plain
                      label={datum?.sourceInstanceName || datum?.source}
                      testId="group-user-source-btn"
                      onClick={() => {
                        return navigate(
                          `/manage-account/organization/external-scim/${datum?.sourceInstance}`
                        )
                      }}
                    />
                  }
                />
              )
            }
          }
        ]
      : [
          {
            property: 'source',
            header: t('common:source'),
            render: (datum) => {
              const source = datum?.[GROUP_SCHEMAS?.PARTIAL]?.source
              return (
                <Typography type="text" testId="group-source">
                  {source || '--'}
                </Typography>
              )
            }
          }
        ])
  ]

  useEffect(() => {
    if (isWorkspace) {
      setHasPermission(
        (!isAssociated && (v2GroupViewPerm || ccsAuthView)) ||
          (isAssociated && (rootWkspReadPerm || ccsAssociateAuthView))
      )
    } else {
      setHasPermission(
        (!isAssociated && v2GroupViewPerm) ||
          (isAssociated && rootWkspReadPermOrgLevel)
      )
    }
  }, [
    isWorkspace,
    isAssociated,
    ccsAuthView,
    v2GroupViewPerm,
    rootWkspReadPermOrgLevel,
    rootWkspReadPerm,
    ccsAssociateAuthView
  ])

  useEffect(() => {
    if (location?.state?.deleteGroupName) {
      const message = (
        <Typography type="text">
          <Trans>
            {t('iam:organization_groups.group_delete_success_msg_v2', {
              groupName: location?.state?.deleteGroupName
            })}
          </Trans>
        </Typography>
      )
      setNotificationInfo(
        message,
        'info',
        t('iam:organization_groups.group_delete_success_msg_title')
      )
    }
  }, [location, t])

  useEffect(() => {
    if (location?.state?.notificationText) {
      const message = <Markdown>{location?.state?.notificationText}</Markdown>
      setNotificationInfo(message, 'info', location?.state?.notificationTitle)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state?.notificationText])

  useEffect(() => {
    let isCurrent = true
    if (hasPermission) {
      const nameSearchFilter = showScimServices
        ? `displayName co "${filter?.trim()}"`
        : `displayName sw "${filter?.trim()}"`
      const filterQuery = showScimServices
        ? `&filter=${encodeURI(nameSearchFilter)}`
        : `?filter=${encodeURI(nameSearchFilter)}`
      const nameQuery = filter?.trim()?.length > 0 ? filterQuery : ''
      setLoading(true)

      const url = showScimServices
        ? `/internal-platform-tenant-ui/v2alpha2/groups?startIndex=${
            (page - 1) * itemsPerPage
          }&count=${itemsPerPage}${nameQuery}`
        : `/identity/v2alpha1/scim/v2/Groups${nameQuery}`
      get(url)
        .then(
          (response) => {
            if (!isCurrent) return
            if (response?.data) {
              setUserGroups(
                showScimServices
                  ? response?.data?.items
                  : response?.data?.Resources
              )
              setTotalItems(
                showScimServices
                  ? response.data.totalResults
                  : response?.data?.Resources?.length
              )
            }
            // Opening modal here instead of in the useEffect to wait for API call to finish before opening modal
            if (location?.state?.createGroup) {
              setShowCreateGroupModal(true)
            }
          },
          (error) => {
            if (!isCurrent) return
            setNotificationInfo(getErrorMessage(error), 'error')
          }
        )
        .finally(() => {
          setLoading(false)
        })
    }
    return () => {
      isCurrent = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, location?.state?.createGroup, page, itemsPerPage, hasPermission])

  return (
    <Box
      data-testid="groups-page"
      pad={{ horizontal: 'xlarge', bottom: 'large' }}
      width="xxlarge"
      alignSelf="center"
    >
      <PageHeader
        responsive
        title={
          <Typography type="heading" level="1" testId="groups-page-title">
            {t('iam:organization_groups.title')}
          </Typography>
        }
        subtitle={
          userGroups?.length && hasPermission ? (
            <Typography
              type="paragraph"
              size="xlarge"
              testId="groups-page-subtitle"
            >
              {isWorkspace
                ? t('iam:workspace_groups.subtitle')
                : t('iam:organization_groups.subtitle')}
            </Typography>
          ) : null
        }
        parent={
          <Anchor
            label={
              isWorkspace
                ? t('iam:workspace_identity.title', {
                    Workspace: t('common:business_object:wkspc_capitalized')
                  })
                : t('iam:organization.governance_title')
            }
            icon={<Previous />}
            href="#"
            onClick={(event) => {
              event.preventDefault()
              handleBackBtnClick()
            }}
            margin={{ bottom: 'xsmall' }}
            testId="groups-back-btn"
          />
        }
        margin={{ bottom: 'medium' }}
      />
      {!isAssociateLoader && hasPermission ? (
        <>
          {userGroups?.length !== 0 || filter?.length > 0 || loading ? (
            <Box width="large">
              <DataTable
                style={{ width: 'fit-content' }}
                summary={{
                  entityName:
                    userGroups?.length === 1
                      ? t('common:item_singular')
                      : t('common:item_plural')
                }}
                grid={{
                  columns,
                  data: userGroups,
                  onClickRow: (event) => {
                    handleRowClick(event?.datum)
                  }
                }}
                loading={loading}
                search={{
                  onSearchValueChange: (value) => {
                    setFilter(value)
                    setPage(1)
                  },
                  placeholder: t('common:search'),
                  width: 'large'
                }}
                pagination={
                  showScimServices && {
                    totalItems: totalItems || 0,
                    itemsPerPage,
                    page,
                    setPage,
                    pageIdxInfo: getPageIndexInfo(),
                    rowCountOptions: [10, 20, 50, 100],
                    setItemsPerPage: (setItems) => {
                      setItemsPerPage(setItems)
                    }
                  }
                }
                button={
                  isWorkspace ? null : (
                    <Box align="end" width="large">
                      {((isAssociated &&
                        v2RbacRootWkspcPolicies?.includes(
                          'identity.user-groups.create'
                        )) ||
                        (!isAssociated &&
                          v2RbacPolicies?.includes(
                            'identity.user-groups.create'
                          ))) && (
                        <Button
                          secondary
                          label={t(
                            'iam:organization_groups.create_group_title'
                          )}
                          onClick={() => {
                            setShowCreateGroupModal(true)
                          }}
                          testId="data-table-create-group-btn"
                        />
                      )}
                    </Box>
                  )
                }
                testId="groups-data-table"
              />
            </Box>
          ) : (
            <Box alignSelf="center" width="large">
              <NoDataInfo
                icon={<Group size="large" />}
                title={t('iam:organization_groups.no_data_title')}
                subtitle={
                  !isWorkspace &&
                  ((isAssociated &&
                    v2RbacRootWkspcPolicies?.includes(
                      'identity.user-groups.create'
                    )) ||
                    (!isAssociated &&
                      v2RbacPolicies?.includes(
                        'identity.user-groups.create'
                      ))) &&
                  t('iam:organization_groups.no_data_subtitle')
                }
                gap="small"
                gapTitleSubtitle="small"
                action={
                  !isWorkspace &&
                  ((isAssociated &&
                    v2RbacRootWkspcPolicies?.includes(
                      'identity.user-groups.create'
                    )) ||
                    (!isAssociated &&
                      v2RbacPolicies?.includes(
                        'identity.user-groups.create'
                      ))) && (
                    <Button
                      primary
                      label={t('iam:organization_groups.create_group_title')}
                      onClick={() => {
                        setShowCreateGroupModal(true)
                      }}
                      testId="no-data-create-group-btn"
                    />
                  )
                }
                testId="group-no-data-info"
              />
            </Box>
          )}
        </>
      ) : (
        <>
          {/* comparing it with false as rbacPolicies are sometimes not loaded in time from visibilityContext and could be undefined/null */}
          {!isAssociateLoader &&
          ((!isAssociated && !hasPermission) ||
            (isAssociated && !hasPermission)) ? (
            <NoViewPermission />
          ) : (
            <Box direction="row" align="center" justify="center">
              <Loader testId="no-permission-loader" />
            </Box>
          )}
        </>
      )}

      {showCreateGroupModal && (
        <CreateGroupModal
          onSetOpen={setShowCreateGroupModal}
          setParentNotification={setNotificationInfo}
        />
      )}
      {notifMsg &&
        displayNotification(notifMsg, notifSeverity, setNotifMsg, notifTitle)}
    </Box>
  )
}

const Groups = ({ isWorkspace = false }) => {
  return (
    <Layout>
      <GroupsContent isWorkspace={isWorkspace} />
    </Layout>
  )
}

GroupsContent.propTypes = {
  /**
   * True if called from v2 workspace, false if called from IGC pages
   */
  isWorkspace: PropTypes.bool
}

Groups.propTypes = {
  /**
   * True if called from v2 workspace, false if called from IGC pages
   */
  isWorkspace: PropTypes.bool
}

export default Groups
